import * as React from 'react';
import { useState, useCallback } from 'react';

import { Spinner, Markdown } from 'components/common';
import { Input } from 'components/bootstrap';
import Modal from 'components/bootstrap/Modal';
import ModalSubmit from 'components/common/ModalSubmit';
import { getPathnameWithoutId } from 'util/URLUtils';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import useAITermsAcceptance from 'ai/hooks/useAITermsAcceptance';
import useAcceptAITerms from 'ai/hooks/useAcceptAITerms';
import type { TelemetryEventType } from 'logic/telemetry/TelemetryContext';

type Props = {
  onClose: () => void;
  onConsent: () => void;
  telemetryEvents: { cancel: TelemetryEventType; consent: TelemetryEventType };
};

const DisclaimerModalContent = ({ onClose, onConsent, telemetryEvents }: Props) => {
  const {
    data: { accepted, terms },
    isLoading,
  } = useAITermsAcceptance();
  const acceptTerms = useAcceptAITerms(onConsent);
  const [checked, setChecked] = useState(false);
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();

  const onCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked);
  }, []);

  const onCancel = useCallback(() => {
    sendTelemetry(telemetryEvents.cancel, {
      app_pathname: getPathnameWithoutId(pathname),
    });

    onClose();
  }, [onClose, pathname, sendTelemetry, telemetryEvents.cancel]);

  const _onConsent = useCallback(async () => {
    sendTelemetry(telemetryEvents.consent, {
      app_pathname: getPathnameWithoutId(pathname),
    });

    await acceptTerms(terms?.version);
    onConsent();
  }, [sendTelemetry, telemetryEvents.consent, pathname, acceptTerms, terms?.version, onConsent]);

  return accepted === false && !isLoading ? (
    <>
      <Modal.Header>
        <h3>Terms and Conditions</h3>
      </Modal.Header>
      <Modal.Body>
        <Markdown text={terms?.text} />
        <Input
          type="checkbox"
          id="consent-AI-Report-disclamer"
          label="By using the AI reporting feature, you acknowledge that you have read, understood, and agreed to the terms outlined in this disclaimer."
          help={
            <span>
              Graylog Germany GmbH reserves the right to modify this disclaimer at any time. Any changes will be
              communicated to users through appropriate channels.
            </span>
          }
          checked={checked}
          onChange={onCheckboxChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <ModalSubmit submitButtonText="Next" onSubmit={_onConsent} onCancel={onCancel} disabledSubmit={!checked} />
      </Modal.Footer>
    </>
  ) : (
    <Spinner />
  );
};

export default DisclaimerModalContent;
